import { useState, useEffect } from "react";
import { Card, CardBody } from "@heroui/react";
import { motion } from "framer-motion";
import { API_ENDPOINTS } from "../config/api";

// SVG icons
const MailIcon = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const LockIcon = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
    />
  </svg>
);

const EyeIcon = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
    />
  </svg>
);

interface SignInProps {
  onLogin: (token: string, user: any) => void;
}

export default function SignIn({ onLogin }: SignInProps) {
  const [mounted, setMounted] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [serverStatus, setServerStatus] = useState<
    "available" | "unavailable" | "checking"
  >("checking");

  const styles = {
    text: {
      color: "#333",
    },
    input: {
      backgroundColor: "#fff",
      borderColor: "#e2e8f0",
      color: "#333",
    },
    button: {
      backgroundColor: "#3b82f6",
      color: "#fff",
    },
    mutedText: {
      color: "#64748b",
    },
    card: {
      backgroundColor: "#fff",
      borderColor: "#e2e8f0",
    },
  };

  // Check server health on component mount
  useEffect(() => {
    checkServerHealth();
  }, []);

  const checkServerHealth = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.HEALTH);
      if (response.ok) {
        const data = await response.json();
        if (data.database === "connected") {
          setServerStatus("available");
        } else {
          setServerStatus("unavailable");
          setError(
            "Database connection is unavailable. Please try again later or contact support."
          );
        }
      } else {
        setServerStatus("unavailable");
        setError("Server is unavailable. Please try again later.");
      }
    } catch (err) {
      setServerStatus("unavailable");
      setError(
        "Cannot connect to the server. Please check your internet connection."
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Check server health before attempting login
      if (serverStatus === "unavailable") {
        await checkServerHealth();
        if (serverStatus === "unavailable") {
          setError(
            "Server or database is currently unavailable. Please try again later."
          );
          setLoading(false);
          return;
        }
      }

      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.status === 503) {
        setError("Database is currently unavailable. Please try again later.");
        setServerStatus("unavailable");
      } else if (response.ok) {
        const data = await response.json();
        if (data && data.token) {
          // Store the token in localStorage
          localStorage.setItem("token", data.token);
          localStorage.setItem("user", JSON.stringify(data.user));

          // Call the onLogin callback
          onLogin(data.token, data.user);
        } else {
          setError("Invalid response from server");
        }
      } else {
        // Parse the error response
        const errorData = await response.json();
        setError(
          errorData.message || errorData.error || "Incorrect email or password"
        );
      }
    } catch (err) {
      console.error("Error during login:", err);
      setError("An error occurred while logging in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-[400px] space-y-6"
      >
        <div className="flex flex-col items-center justify-center">
          <div className="mb-3">
            <h1 className="text-2xl font-bold text-blue-600">
              Google Sheets Scraper
            </h1>
          </div>
          <div className="text-center space-y-2">
            <h1
              className="text-2xl font-semibold tracking-tight"
              style={styles.text}
            >
              Welcome back
            </h1>
            <p style={styles.mutedText} className="text-sm">
              Sign in to your account
            </p>
          </div>
        </div>

        <Card
          className="backdrop-blur-lg backdrop-saturate-150 border border-gray-200"
          style={styles.card}
        >
          <CardBody className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm"
                >
                  {error}
                </motion.div>
              )}

              {serverStatus === "unavailable" && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-700 text-sm flex items-center space-x-2"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span>
                    Server connection issues detected. You may experience
                    problems signing in.
                  </span>
                </motion.div>
              )}

              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="text-sm font-medium"
                  style={styles.text}
                >
                  Email
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <MailIcon />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    style={styles.input}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="text-sm font-medium"
                  style={styles.text}
                >
                  Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <LockIcon />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full pl-10 pr-11 py-2 rounded-lg border transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    style={styles.input}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={loading || serverStatus === "unavailable"}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full py-2.5 rounded-lg focus:ring-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 bg-blue-600 text-white focus:ring-blue-500/20"
              >
                {loading ? "Signing in..." : "Sign in"}
              </motion.button>

              {serverStatus === "unavailable" && (
                <button
                  type="button"
                  onClick={checkServerHealth}
                  className="w-full py-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  Check server status again
                </button>
              )}
            </form>
          </CardBody>
        </Card>
      </motion.div>
    </div>
  );
}
